import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet } from 'react-router-dom'
import { Spin } from '@/ui/compat/antd'
import {
  ensureValidAccessToken,
  stopProactiveTokenRefresh,
} from './config/api.config'
import { clearAuthSession } from './utils/authUtils'

const LOGIN_PATH = '/login'

const AuthCheckingFallback = () => {
  const { t } = useTranslation()
  return (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
    aria-busy='true'
    aria-label={t('common.sessionCheck')}
  >
    <Spin size='large' />
  </div>
  )
}

const AuthenticatedRoute = () => {
  // Optimistic: if a token already exists (e.g. just after login), render children
  // immediately and verify in the background — avoids full-page spin remount.
  const [status, setStatus] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('accessToken')
      ? 'valid'
      : 'checking'
  )

  useEffect(() => {
    let cancelled = false

    const verifySession = async () => {
      try {
        const isValid = await ensureValidAccessToken()
        if (cancelled) return

        if (!isValid) {
          try {
            clearAuthSession()
            stopProactiveTokenRefresh()
          } catch {
            // ignore cleanup errors
          }
          setStatus('invalid')
          return
        }

        setStatus('valid')
      } catch {
        if (cancelled) return
        try {
          clearAuthSession()
          stopProactiveTokenRefresh()
        } catch {
          // ignore cleanup errors
        }
        setStatus('invalid')
      }
    }

    verifySession()

    return () => {
      cancelled = true
    }
  }, [])

  if (status === 'checking') return <AuthCheckingFallback />
  if (status === 'invalid') return <Navigate replace to={LOGIN_PATH} />

  return <Outlet />
}

export default AuthenticatedRoute
